import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pie, PieChart, ResponsiveContainer, Sector } from 'recharts';
import { colorVarOf } from './shared.js';
import type { DesignerKpi } from './shared.js';

interface DonutBlockProps {
  total: number;
  data: DesignerKpi[];
  active: string | null;
  onSelect: (next: string | null) => void;
}

/**
 * デザイナー別 actual 値の構成比 donut chart。クリックで active を切替、hover で focused を更新する。
 * 中央に focused / total の数値とラベルを重ねて表示する。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business IDT KPI ページの donut chart panel。デザイナー別 actual の構成比 + クリックフィルタ
 * @graph-connects none
 */
export function DonutBlock({ total, data, active, onSelect }: DonutBlockProps) {
  const { t } = useTranslation();
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const focused = active
    ? (data.find((d) => d.id === active) ?? null)
    : hoveredId
      ? (data.find((d) => d.id === hoveredId) ?? null)
      : null;
  const focusedPct = focused ? ((focused.actual / total) * 100).toFixed(1) : null;

  return (
    <div
      className="flex flex-col items-center gap-2 -my-2"
      onMouseLeave={() => {
        setHoveredId(null);
      }}
    >
      <div className="relative w-[300px] h-[300px] select-none" style={{ outline: 'none' }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={data}
              dataKey="actual"
              nameKey="id"
              innerRadius={78}
              outerRadius={118}
              paddingAngle={1.2}
              startAngle={90}
              endAngle={-270}
              isAnimationActive
              animationDuration={1100}
              animationBegin={250}
              animationEasing="ease-out"
              onClick={(payload, _idx, evt) => {
                const id = (payload as { id?: string } | undefined)?.id;
                if (!id) return;
                const target = (evt as unknown as { currentTarget?: { blur?: () => void } })
                  .currentTarget;
                target?.blur?.();
                onSelect(active === id ? null : id);
              }}
              onMouseEnter={(payload) => {
                const id = (payload as { id?: string } | undefined)?.id;
                if (id) setHoveredId(id);
              }}
              onMouseLeave={() => {
                setHoveredId(null);
              }}
              style={{ cursor: 'pointer', outline: 'none' }}
              tabIndex={-1}
              shape={(props: unknown) => {
                const p = props as {
                  cx: number;
                  cy: number;
                  innerRadius: number;
                  outerRadius: number;
                  startAngle: number;
                  endAngle: number;
                  payload: DesignerKpi;
                };
                const id = p.payload.id;
                const isClickActive = id === active;
                const isHovered = hoveredId === id;
                const isExpanded = isClickActive || (active === null && isHovered);
                const isMuted = active !== null && !isClickActive;
                return (
                  <g
                    style={{
                      transform: isExpanded ? 'scale(1.07)' : 'scale(1)',
                      transformOrigin: `${String(p.cx)}px ${String(p.cy)}px`,
                      transition:
                        'transform 0.7s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.45s ease',
                      opacity: isMuted ? 0.16 : 1,
                      willChange: 'transform',
                    }}
                  >
                    <Sector
                      cx={p.cx}
                      cy={p.cy}
                      innerRadius={p.innerRadius}
                      outerRadius={p.outerRadius}
                      startAngle={p.startAngle}
                      endAngle={p.endAngle}
                      fill={`var(${colorVarOf(id)})`}
                      stroke="var(--color-bg-surface)"
                      strokeWidth={2}
                      style={{
                        outline: 'none',
                        vectorEffect: 'non-scaling-stroke',
                      }}
                    />
                  </g>
                );
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <div
            className="text-[10px] uppercase tracking-wider transition-colors duration-300"
            style={{
              color: focused ? `var(${colorVarOf(focused.id)})` : 'var(--color-text-muted)',
            }}
          >
            {focused ? focused.id : t('idtKpi.donut.totalLabel')}
          </div>
          <div
            className="text-3xl font-black tabular-nums tracking-tight mt-1 transition-colors duration-300"
            style={{
              color: focused ? `var(${colorVarOf(focused.id)})` : 'var(--color-text-primary)',
            }}
          >
            {(focused ? focused.actual : total).toLocaleString()}
          </div>
          <div className="text-[11px] text-[var(--color-text-muted)] mt-1 tabular-nums">
            {focusedPct
              ? t('idtKpi.donut.shareOfTotal', { pct: focusedPct })
              : t('idtKpi.donut.hint')}
          </div>
        </div>
      </div>
    </div>
  );
}
